import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FlightRow from './FlightRow';
import FlightCarousel from './FlightCarousel';
import './FlightSection.css';

const FlightSection = ({ type, flights, category }) => {
  const title = type === 'arrivals' ? 'ARRIVALS' : 'DEPARTURES';
  const colorClass = type === 'arrivals' ? 'arrivals' : 'departures';

  // Group flights: associate codeshare flights ("Other" flights with missing data) 
  // with the immediately preceding primary flight
  const processedFlights = useMemo(() => {
    const result = [];
    
    for (let i = 0; i < flights.length; i++) {
      const flight = flights[i];
      
      // Check if this is a codeshare flight (missing data or "Other" airline)
      const hasMissingData = (!flight.destination && !flight.origin) || !flight.scheduledTime;
      const isOtherFlight = flight.airlineCode === 'Other' || !flight.airlineCode;
      const isCodeshare = hasMissingData || isOtherFlight;
      
      if (isCodeshare) {
        // This is a codeshare flight - try to associate it with the previous flight
        if (result.length > 0) {
          const lastItem = result[result.length - 1];
          
          // If the last item is a group, add to it
          if (lastItem.isGroup) {
            lastItem.flights.push(flight);
          } 
          // If the last item is a single flight with complete data, convert it to a group
          else if (lastItem.destination || lastItem.origin || lastItem.scheduledTime) {
            result[result.length - 1] = {
              isGroup: true,
              flights: [lastItem, flight]
            };
          }
          // Otherwise, just add as a new group
          else {
            result.push({ isGroup: true, flights: [flight] });
          }
        } else {
          // No previous flight, create a new group
          result.push({ isGroup: true, flights: [flight] });
        }
      } else {
        // This is a primary flight with complete data
        result.push(flight);
      }
    }
    
    // Convert single-item groups back to regular flights
    return result.map(item => {
      if (item.isGroup && item.flights.length === 1) {
        return item.flights[0];
      }
      return item;
    });
  }, [flights]);

  // Group flights by date, then sort by time within each date
  const flightsByDate = processedFlights.reduce((acc, flight) => {
    const date = (flight.isGroup ? flight.flights[0].date : flight.date) || 'Unknown Date';
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(flight);
    return acc;
  }, {});
  
  // Sort dates: show today first, then tomorrow
  // Parse dates to compare them properly instead of alphabetically
  const parseDate = (dateStr) => {
    if (dateStr === 'Unknown Date') return null;
    // Parse format like "Sunday 16 Nov, 2025"
    const months = {
      'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
      'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
    };
    const parts = dateStr.match(/(\w+)\s+(\d{1,2})\s+(\w+),\s*(\d{4})/);
    if (!parts) return null;
    const [, , day, month, year] = parts;
    return new Date(parseInt(year), months[month], parseInt(day));
  };
  
  const sortedDates = Object.keys(flightsByDate).sort((a, b) => {
    if (a === 'Unknown Date') return 1;
    if (b === 'Unknown Date') return -1;
    const dateA = parseDate(a);
    const dateB = parseDate(b);
    if (!dateA) return 1;
    if (!dateB) return -1;
    return dateA - dateB; // Sort chronologically (today first, then tomorrow)
  }).slice(0, 2); // Only show 2 days
  
  // Sort flights within each date by scheduled time
  // Flights with missing time should go to the bottom
  sortedDates.forEach(date => {
    flightsByDate[date].sort((a, b) => {
      const flightA = a.isGroup ? a.flights[0] : a;
      const flightB = b.isGroup ? b.flights[0] : b;
      
      const timeA = flightA.scheduledTime || '';
      const timeB = flightB.scheduledTime || '';
      
      // If one has time and the other doesn't, the one without time goes to bottom
      if (timeA && !timeB) return -1;
      if (!timeA && timeB) return 1;
      if (!timeA && !timeB) return 0; // Both missing time, keep order
      
      // Both have time, sort normally
      return timeA.localeCompare(timeB);
    });
  });

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`flight-section ${colorClass}`}
    >
      <div className="section-header">
            <h2 className="section-title">{title}</h2>
            <img 
              src={type === 'arrivals' ? '/plane-landing.png' : '/departure.png'} 
              alt={type === 'arrivals' ? 'Arrivals' : 'Departures'}
              className="section-icon"
            />
          </div>
      
      <div className="flight-table-container">
        <div className="flight-table-header">
          <div className="col-flight">FLIGHT</div>
          <div className="col-origin">DESTINATION</div>
          <div className="col-time">TIME</div>
          <div className="col-estimated">ESTM</div>
          <div className="col-terminal">TERMINAL</div>
          <div className="col-status">STATUS</div>
        </div>
        
        <div className="flight-table-body">
          <AnimatePresence mode="popLayout">
            {sortedDates.length > 0 ? (
              sortedDates.map((date, dateIndex) => (
                <div key={date} className="date-group">
                  <div className="date-header">{date}</div>
                  {flightsByDate[date].map((flight, flightIndex) => {
                    const globalIndex = sortedDates.slice(0, dateIndex).reduce((sum, d) => sum + flightsByDate[d].length, 0) + flightIndex;
                    
                    // If this is a group of flights, use carousel
                    if (flight.isGroup && flight.flights.length > 1) {
                      const hasFinalCall = flight.flights.some(f => f.status?.toUpperCase().includes('FINAL CALL'));
                      return (
                        <div key={`group-${globalIndex}`} className={`flight-row ${hasFinalCall ? 'final-call-row' : ''}`}>
                          <FlightCarousel 
                            flights={flight.flights}
                            delay={3000}
                            index={globalIndex}
                            type={type}
                          />
                        </div>
                      );
                    }
                    
                    // Regular single flight
                    const actualFlight = flight.isGroup ? flight.flights[0] : flight;
                    return (
                      <FlightRow 
                        key={`${actualFlight.flightNumber}-${actualFlight.scheduledTime}-${globalIndex}`}
                        flight={actualFlight}
                        index={globalIndex}
                        type={type}
                      />
                    );
                  })}
                </div>
              ))
            ) : (
              <div className="no-flights">
                <p>No {type} scheduled</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default FlightSection;

